import type { CustomFieldInputType } from "@/api/db/connection";
import { Normalize } from "@/api/normalize/normalize";
import { djs } from "@/lib/djs";

const CUSTOM_FIELD_INPUT_TYPES = [
	"text",
	"date",
	"time",
	"currency",
	"number",
	"percentage",
	"cpf",
	"phone",
] as const;

function digits(value: string, limit?: number) {
	const normalized = value.replaceAll(/\D/gu, "");

	return limit === undefined ? normalized : normalized.slice(0, limit);
}

function groupedInteger(value: string) {
	return value.replaceAll(/\B(?=(\d{3})+(?!\d))/gu, ".");
}

function dateInput(value: string) {
	const valueDigits = digits(value, 8);
	const parts = [valueDigits.slice(0, 2), valueDigits.slice(2, 4), valueDigits.slice(4)];

	return parts.filter((part) => part !== "").join("/");
}

function currencyInput(value: string) {
	const valueDigits = digits(value).replace(/^0+(?=\d)/u, "");

	if (valueDigits === "") {
		return "";
	}

	const padded = valueDigits.padStart(3, "0");
	const integer = padded.slice(0, -2).replace(/^0+(?=\d)/u, "");

	return `R$ ${groupedInteger(integer)},${padded.slice(-2)}`;
}

function timeInput(value: string) {
	const valueDigits = digits(value, 4);

	if (valueDigits.length <= 2) {
		return valueDigits;
	}

	return `${valueDigits.slice(0, 2)}:${valueDigits.slice(2)}`;
}

function decimalInput(value: string) {
	const normalized = value.replaceAll(".", ",").replaceAll(/[^\d,]/gu, "");
	const separator = normalized.indexOf(",");

	if (separator === -1) {
		return normalized;
	}

	return `${normalized.slice(0, separator)},${normalized.slice(separator + 1).replaceAll(",", "")}`;
}

function cpfInput(value: string) {
	const valueDigits = digits(value, 11);
	const first = valueDigits.slice(0, 3);
	const second = valueDigits.slice(3, 6);
	const third = valueDigits.slice(6, 9);
	const check = valueDigits.slice(9);

	return `${first}${second === "" ? "" : `.${second}`}${third === "" ? "" : `.${third}`}${check === "" ? "" : `-${check}`}`;
}

function phoneInput(value: string) {
	const valueDigits = digits(value, 11);

	if (valueDigits.length <= 2) {
		return valueDigits === "" ? "" : `(${valueDigits}`;
	}

	const middleSize = valueDigits.length > 10 ? 5 : 4;
	const middle = valueDigits.slice(2, 2 + middleSize);
	const end = valueDigits.slice(2 + middleSize);

	return `(${valueDigits.slice(0, 2)}) ${middle}${end === "" ? "" : `-${end}`}`;
}

function decimalCanonical(value: string) {
	const normalized = value.includes(",") ? value.replaceAll(".", "").replace(",", ".") : value;

	if (!/^\d+(\.\d+)?$/u.test(normalized)) {
		return null;
	}

	return normalized;
}

const maskByType: Record<CustomFieldInputType, (value: string) => string> = {
	text: (value) => value,
	date: dateInput,
	time: timeInput,
	currency: currencyInput,
	number: decimalInput,
	percentage: (value) => {
		const decimal = decimalInput(value);

		return decimal === "" ? "" : `${decimal}%`;
	},
	cpf: cpfInput,
	phone: phoneInput,
};

const canonicalByType: Record<CustomFieldInputType, (value: string) => string | null> = {
	text: (value) => value,
	date: (value) => {
		const iso = djs(value, "YYYY-MM-DD", true);

		if (iso.isValid()) {
			return iso.format("YYYY-MM-DD");
		}

		const local = djs(value, "DD/MM/YYYY", true);

		return local.isValid() ? local.format("YYYY-MM-DD") : null;
	},
	time: (value) => {
		const normalized = /^\d{4}$/u.test(value) ? `${value.slice(0, 2)}:${value.slice(2)}` : value;

		if (!/^([01]\d|2[0-3]):[0-5]\d$/u.test(normalized)) {
			return null;
		}

		return normalized;
	},
	currency: (value) => {
		if (/^\d+$/u.test(value)) {
			return value.replace(/^0+(?=\d)/u, "");
		}

		const valueDigits = digits(value);

		return valueDigits === "" ? null : valueDigits.replace(/^0+(?=\d)/u, "");
	},
	number: decimalCanonical,
	percentage: (value) => {
		const canonical = decimalCanonical(value.replace("%", ""));

		if (canonical === null || Number(canonical) > 100) {
			return null;
		}

		return canonical;
	},
	cpf: (value) => {
		const cpf = Normalize.cpf(value);

		return cpf?.valid === true ? cpf.norm : null;
	},
	phone: (value) => {
		const phone = Normalize.phone(value);

		if (typeof phone === "string" || phone?.country_code !== "55") {
			return null;
		}

		return phone.norm;
	},
};

const formatByType: Record<CustomFieldInputType, (canonical: string) => string> = {
	text: (canonical) => canonical,
	date: (canonical) => djs(canonical, "YYYY-MM-DD", true).format("DD/MM/YYYY"),
	time: (canonical) => canonical,
	currency: currencyInput,
	number: (canonical) => canonical.replace(".", ","),
	percentage: (canonical) => `${canonical.replace(".", ",")}%`,
	cpf: cpfInput,
	phone: phoneInput,
};

export const CustomFieldValues = {
	inputTypes: CUSTOM_FIELD_INPUT_TYPES,

	mask(inputType: CustomFieldInputType, value: string) {
		return maskByType[inputType](value);
	},

	canonical(inputType: CustomFieldInputType, value: string) {
		const trimmed = value.trim();

		if (trimmed === "") {
			return null;
		}

		return canonicalByType[inputType](trimmed);
	},

	format(inputType: CustomFieldInputType, canonical: string) {
		return formatByType[inputType](canonical);
	},
};
